import { useState, useCallback } from "react";
import { mapsApi, BoundingBox, MapGeometry } from "../services/mapsApi";
import type { CountryCode, MapType } from "../config/countries";

interface UseMapDataState {
  geometries: MapGeometry[];
  loading: boolean;
  error: string | null;
}

export const useMapData = () => {
  const [state, setState] = useState<UseMapDataState>({
    geometries: [],
    loading: false,
    error: null,
  });

  const fetchMapData = useCallback(
    async (boundingBox: BoundingBox, mapType: MapType | undefined, countryCode: CountryCode, catalogue = false, zoom?: number) => {
      if (!mapType) return;

      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await mapsApi.getMapData(mapType, boundingBox, countryCode, catalogue, zoom);
        if (!response.results || !Array.isArray(response.results)) {
          throw new Error("Invalid API response format");
        }

        setState((prev) => ({
          ...prev,
          geometries: response.results,
          loading: false,
        }));
      } catch (error) {
        console.error("Error fetching map data:", error);
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error ? error.message : "Failed to fetch map data",
          loading: false,
        }));
      }
    },
    []
  );

  const clearData = useCallback(() => {
    setState({
      geometries: [],
      loading: false,
      error: null,
    });
  }, []);

  const setGeometries = useCallback((geometries: MapGeometry[]) => {
    setState((prev) => ({
      ...prev,
      geometries,
      error: null,
    }));
  }, []);

  return {
    ...state,
    fetchMapData,
    clearData,
    setGeometries,
  };
};
